import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, concatMap, exhaustMap, Observable, switchMap, tap } from 'rxjs';
import { PatientService } from './patient.service';
import { SocketService } from './socket.service';
import { KEY_CONSENT_SEARCH_TERMS, NEGATIVE_KEYWORDS, POSITIVE_KEYWORDS } from '../data/app.data';
import * as moment from 'moment';
import * as _ from 'lodash';
import { jwtDecode } from 'jwt-decode';

@Injectable({
  providedIn: 'root'
})
export class PatientConsentService {
  private apiUrl = `api/consent`;
  private patientConsentSubject = new BehaviorSubject<any[] | null>(null);
  private editingConsentSubject = new BehaviorSubject<any>(null);
  private ignoreListSubject = new BehaviorSubject<string[]>([]);

  patientConsents$ = this.patientConsentSubject.asObservable();
  editingConsent$ = this.editingConsentSubject.asObservable();

  patientId!: string;

  constructor(
    private http: HttpClient,
    private patientService: PatientService,
    private socketService: SocketService
  ) {
    //this.subscribeToCurrentPatient();
    this.subscribeToCurrentPatient();
    this.setupSocketListeners();
  }

  //ngOnInit() {
  // this.subscribeToCurrentPatient();
  // this.setupSocketListeners();
  //}
  private subscribeToCurrentPatient(): void {

    this.patientService.currentPatient$.pipe(
      concatMap(patient => {
        if (patient) {
          this.patientId = patient.id;
          this.patientConsentSubject.next([]);
          return this.loadConsentsByPatient(patient.id);
        } else {
          // If there's no current patient, clear the consents
          this.patientConsentSubject.next([]);
          return []; // Return an empty array to satisfy the Observable return type
        }
      }),
      tap((results: any) => {
        // Update the BehaviorSubject with the new or cleared notes
        const consents = results ? results.data : [];
        this.patientConsentSubject.next(consents);
      })
    ).subscribe();
  }

  private setupSocketListeners(): void {

    // Current patient's ID
    let currentPatientId: string | null = null;

    // Subscribe to the current patient changes to keep track of the current patient's ID
    this.patientService.currentPatient$.subscribe(patient => {
      currentPatientId = patient ? patient.id : null;
    });

    // Listener for when a new consent is added
    this.socketService.listen('consentAdded', 'patient-consent-service').subscribe(consent => {
      if (consent.patientId === currentPatientId) {
        const current = this.patientConsentSubject.getValue() || [];
        this.patientConsentSubject.next([...current!, consent]);
      }
    });

    // Listener for when a consent is updated
    this.socketService.listen('consentUpdated', 'patient-consent-service').subscribe(updatedConsent => {
      if (updatedConsent.patientId === currentPatientId) {
        const current = this.patientConsentSubject.getValue() || [];
        const updated = current!.map(consent => {
          if (consent.id === updatedConsent.id) {
            // Preserve processingStatus from existing media when updating
            const existingMedia = consent.media || [];
            const updatedMedia = (updatedConsent.media || []).map((newMedia: any) => {
              const existingMediaItem = existingMedia.find((m: any) => m.id === newMedia.id);
              if (existingMediaItem && existingMediaItem.processingStatus === 'processing') {
                return { ...newMedia, processingStatus: existingMediaItem.processingStatus };
              }
              return newMedia;
            });
            return {
              ...updatedConsent,
              media: updatedMedia
            };
          }
          return consent;
        });
        this.patientConsentSubject.next(updated);
      }
    });

    // Listener for when a consent is deleted
    this.socketService.listen('consentDeleted', 'patient-consent-service').subscribe(deletedConsent => {
      if (deletedConsent.patientId === currentPatientId) {
        const current = this.patientConsentSubject.getValue() || [];
        const updated = current!.filter(consent => consent.id !== deletedConsent.id);
        this.patientConsentSubject.next(updated);
      }
    });

    // Listener for when consent PDF conversion starts
    this.socketService.listen('consentPdfConversionStarted', 'patient-consent-service').subscribe((data: any) => {
      if (data.patientId === currentPatientId && data.mediaId) {
        const current = this.patientConsentSubject.getValue() || [];
        const consentIndex = current.findIndex(consent => consent.id === data.consentId);
        
        if (consentIndex >= 0) {
          const mediaList = current[consentIndex].media as any[] || [];
          const mediaIndex = mediaList.findIndex(m => m.id === data.mediaId);
          
          if (mediaIndex >= 0) {
            // Mark PDF as processing
            mediaList[mediaIndex] = {
              ...mediaList[mediaIndex],
              processingStatus: 'processing'
            };
            current[consentIndex] = {
              ...current[consentIndex],
              media: mediaList
            };
            this.patientConsentSubject.next([...current]);
          }
        }
      }
    });

    // Listener for when consent PDF conversion completes
    this.socketService.listen('consentPdfConversionCompleted', 'patient-consent-service').subscribe((data: any) => {
      if (data.patientId === currentPatientId) {
        // The consentUpdated event will handle the full update with new images
        // This event is mainly for tracking/logging purposes
        // We could also update processing status here if needed
        const current = this.patientConsentSubject.getValue() || [];
        const consentIndex = current.findIndex(consent => consent.id === data.itemId);
        
        if (consentIndex >= 0) {
          const mediaList = current[consentIndex].media as any[] || [];
          // Remove processing status from PDF media
          const updatedMedia = mediaList.map(m => {
            if (m.mediaType === 'application/pdf' && m.processingStatus === 'processing') {
              return { ...m, processingStatus: undefined };
            }
            return m;
          });
          current[consentIndex] = {
            ...current[consentIndex],
            media: updatedMedia
          };
          this.patientConsentSubject.next([...current]);
        }
      }
    });
  }

  loadConsentsByPatient(patientId: string): Observable<any[]> {
    this.patientConsentSubject.next(null);
    const token = localStorage.getItem('token');

    return this.http.get<any[]>(`${this.apiUrl}/patient/${patientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }


  addConsent(data: any): Observable<any> {
    data.forEach((value: any, key: any) => {
      console.log(key, value);
    });
    const token = localStorage.getItem('token');
    return this.http.post<any>(this.apiUrl, data , { headers: { Authorization: `Bearer ${token}` } })
      .pipe(
        switchMap(() => this.loadConsentsByPatient(this.patientId)),
        tap((results: any) => {
          const consents = results ? results.data : [];
          this.patientConsentSubject.next(consents);
        })
      );
  }


  updateBulkConsent(data: any): Observable<any> {

    const token = localStorage.getItem('token');

    return this.http.post<any>(`${this.apiUrl}/bulk`,  data , {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      //tap(response => console.log('API Response:', response)),
      switchMap(() => this.loadConsentsByPatient(this.patientId)),
      tap((results: any) => {
        // Update the BehaviorSubject with the new or cleared notes
        const consents = results ? results.data : [];
        this.patientConsentSubject.next(consents);
      })
    );
  }


  emailConsent(data: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/email`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  getConsentById(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get<any>(`${this.apiUrl}/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  updateConsent(id: string, data: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put<any>(`${this.apiUrl}/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      switchMap(() => this.loadConsentsByPatient(this.patientId)),
      tap((results: any) => {
        // Update the BehaviorSubject with the new or cleared notes
        const consents = results ? results.data : [];
        this.patientConsentSubject.next(consents);
      })
    );
  }

  deleteConsent(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.delete(`${this.apiUrl}/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      switchMap(() => this.loadConsentsByPatient(this.patientId)),
      tap((results: any) => {
        // Update the BehaviorSubject with the new or cleared notes
        const consents = results ? results.data : [];
        this.patientConsentSubject.next(consents);
      })
    );
  }

  deleteConsentMedia(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.delete(`${this.apiUrl}/media/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  checkUpcomingTheatreConsents(groupId: string): Observable<any> {
    const token = localStorage.getItem('token');
    const params = {
      groupId: groupId
    };
    return this.http.get<any>(`${this.apiUrl}/check-upcoming-theatre`, {
      headers: { Authorization: `Bearer ${token}` },
      params: params
    });
  }

  get ignoreList() {
    return this.ignoreListSubject.getValue();
  }

  ignoreSelected(patientId: string) {
    const ignored = this.ignoreListSubject.getValue();
    ignored.push(patientId);
    this.ignoreListSubject.next(ignored);
  }

  setEditingConsent(consent: any | null) {
    this.editingConsentSubject.next(consent);
  }

  analyzeConsentConditions(consents: any): any | undefined {
    if (!consents?.length) return;

    // 1) Filter consents where consentGiven = true, then sort them descending by date
    const sortedConsents = _(consents)
      .filter((consent) => consent.consentGiven)
      .orderBy((consent) => moment(consent.updatedAt).toDate(), 'desc')
      .value();

    // 2) Build an array of bracketed items for each consent
    const analyzedConsents = sortedConsents.map((consent) => {
      const requiredItems = this.extractConsentItems(consent.consentResult, 'REQUIRED CONSENT');
      const optionalItems = [
        ...this.extractConsentItems(consent.consentResult, 'OPTIONAL CONSENT'),
        ...this.extractConsentItems(consent.consentResult, 'CONDITIONAL CONSENT')
      ];

      // categorize them (positive/negative, etc.)
      const categorizedRequired = this.categorizeConsentItems(requiredItems);
      const categorizedOptional = this.categorizeConsentItems(optionalItems);

      // extract top-level fields (blood transfusion, procedure side, etc.)
      const bloodTransfusionValue = this.extractBloodTransfusion(consent.consentResult);
      const procedureSideValue = this.extractProcedureSide(consent.consentResult);
      const selectedImplantValue = this.extractSelectedImplant(consent.consentResult);

      // add date + top-level fields to each item
      const withDateRequired = categorizedRequired.map((item) => ({
        ...item,
        consentUpdatedAt: consent.updatedAt,
        shortText: this.extractShortText(item.text),
        bloodTransfusion: bloodTransfusionValue,
        procedureSide: procedureSideValue,
        selectedImplant: selectedImplantValue
      }));

      const withDateOptional = categorizedOptional.map((item) => ({
        ...item,
        consentUpdatedAt: consent.updatedAt,
        shortText: this.extractShortText(item.text),
        bloodTransfusion: bloodTransfusionValue,
        procedureSide: procedureSideValue,
        selectedImplant: selectedImplantValue
      }));

      return {
        ...consent,
        requiredConsentItems: withDateRequired,
        optionalConsentItems: withDateOptional,
        // optionally store them at the parent level too:
        bloodTransfusion: bloodTransfusionValue,
        procedureSide: procedureSideValue,
        selectedImplant: selectedImplantValue
      };
    });

    // 3) Remove any consents that have no bracketed items
    const filteredConsents = analyzedConsents.filter(
      (c) => c.requiredConsentItems.length > 0 || c.optionalConsentItems.length > 0
    );

    // 4) Flatten all bracketed items from all consents
    const allRequiredConsentItems = _(filteredConsents)
      .map('requiredConsentItems')
      .flatten()
      .value();

    const allOptionalConsentItems = _(filteredConsents)
      .map('optionalConsentItems')
      .flatten()
      .value();

    const combinedAllItems = [...allRequiredConsentItems, ...allOptionalConsentItems];

    // 5) Remove duplicates (based on text), keeping the item with the most recent updatedAt
    const uniqueMostRecent = _(combinedAllItems)
      .groupBy('text')
      .mapValues((items) => {
        return _.maxBy(items, (item) => new Date(item.consentUpdatedAt).getTime());
      })
      .values()
      .value();

    // 6) Pick the MOST RECENT values for procedureSide, selectedImplant, bloodTransfusion
    //    (i.e., the first non-null we find in sortedConsents, which are descending by date)
    let mostRecentProcedureSide: string | null = null;
    let mostRecentImplant: string | null = null;
    let mostRecentBloodTransfusion: 'YES' | 'NO' | null = null;

    for (const c of uniqueMostRecent) {
      // If we haven't found a procedure side yet, and this consent has one, use it
      if (!mostRecentProcedureSide && c.procedureSide) {
        mostRecentProcedureSide = c.procedureSide;
      }
      if (!mostRecentImplant && c.selectedImplant) {
        mostRecentImplant = c.selectedImplant;
      }
      if (!mostRecentBloodTransfusion && c.bloodTransfusion) {
        mostRecentBloodTransfusion = c.bloodTransfusion;
      }

      // If we have them all, no need to keep looking
      if (mostRecentProcedureSide && mostRecentImplant && mostRecentBloodTransfusion) {
        break;
      }
    }

    // 7) Return a single object that includes:
    //    - items: the bracketed items,
    //    - procedureSide, selectedImplant, bloodTransfusion
    return {
      items: uniqueMostRecent,
      procedureSide: mostRecentProcedureSide,
      selectedImplant: mostRecentImplant,
      bloodTransfusion: mostRecentBloodTransfusion,
    };
  }



  private extractProcedureSide(consentText: string | null | undefined): string | null {
    if (!consentText) return null;

    // Capture whatever appears after "PROCEDURE SIDE:" on that same line
    const regex = /procedure side:\s*([^\r\n]+)/i;
    const match = regex.exec(consentText);

    // If found, we return the captured value (trimmed), otherwise null
    return match ? match[1].trim() : null;
  }

  private extractSelectedImplant(consentText: string | null | undefined): string | null {
    if (!consentText) return null;

    // Capture whatever appears after "SELECTED IMPLANT:" on that same line
    const regex = /selected implant:\s*([^\r\n]+)/i;
    const match = regex.exec(consentText);

    return match ? match[1].trim() : null;
  }


  private extractBloodTransfusion(consentText: string | null | undefined): 'YES' | 'NO' | null {
    if (!consentText) return null;

    // Looks for a line containing "BLOOD TRANSFUSION: YES" or "BLOOD TRANSFUSION: NO" (case-insensitive).
    const regex = /blood transfusion:\s*(yes|no)/i;
    const match = regex.exec(consentText);

    // Convert to upper-case "YES" or "NO" if found, otherwise return null.
    return match ? (match[1].toUpperCase() as 'YES' | 'NO') : null;
  }

  private extractShortText(fullText: string): string {

    if (!fullText) return '';

    const textLower = fullText.toLowerCase();

    // Check each KEY_TERMS entry in order
    for (const entry of KEY_CONSENT_SEARCH_TERMS) {
      if (textLower.includes(entry.keyword.toLowerCase())) {
        return entry.label;
      }
    }
    return '';
  }




  /**
 * A simple helper to categorize each consent item as positive or negative, etc.
 *
 * - A real-world sentiment approach would be more complex, but here's a placeholder example:
 *   - If the item is [x] (checked), we generally consider it "positive", 
 *     unless it includes certain negative keywords.
 *   - If the item is [ ] (unchecked), we consider it "negative" or maybe "neutral",
 *     unless the text obviously indicates acceptance or something else.
 */
  private categorizeConsentItems(
    items: Array<{ text: string; checked: boolean }>
  ): Array<{ text: string; checked: boolean; category: string }> {

    return items.map(item => {
      const textLower = item.text.toLowerCase();
      let category = 'neutral';

      if (item.checked) {
        // If user ticked [x], treat it as “positive” unless we see obviously negative words
        const hasNegative = NEGATIVE_KEYWORDS.some(word => textLower.includes(word));
        category = hasNegative ? 'negative' : 'positive';
      } else {
        // If user did NOT check [ ], treat it as “negative” unless we see obviously positive words
        const hasPositive = POSITIVE_KEYWORDS.some(word => textLower.includes(word));
        category = hasPositive ? 'positive' : 'negative';
      }

      return { ...item, category };
    });
  }

  /**
   * Helper function to extract lines in the form of “[x] Some text” or “[ ] Some text”
   * within a specific heading section (e.g., "REQUIRED CONSENT", "OPTIONAL CONSENT").
   */
  private extractConsentItems(
    consentText: string | null | undefined,
    sectionHeading: string
  ): Array<{ text: string; checked: boolean }> {
    if (!consentText) return [];

    // Regex to capture content between the given heading and the next possible heading
    const headingRegex = new RegExp(
      `${sectionHeading}([\\s\\S]*?)(?=REQUIRED CONSENT|OPTIONAL CONSENT|CONDITIONAL CONSENT|$)`,
      'i'
    );

    const match = headingRegex.exec(consentText);
    if (!match || match.length < 2) return [];

    const sectionContent = match[1];

    return sectionContent
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.startsWith('[x]') || line.startsWith('[ ]'))
      .map((line) => {
        const checked = line.startsWith('[x]');
        const text = line.replace(/^\[[x ]\]\s*/, '');
        return { text, checked };
      });
  }
}
